import { runBatch } from '../../../utils/office/document';
import { splitEdgeSpacing } from '../../../utils/office/selection';

/** Word highlight name used for placeholders. Exported so the pane buttons can
 *  render themselves in the colour they actually insert (see TextEditing.tsx). */
export const PLACEHOLDER_HIGHLIGHT = 'Yellow';

/**
 * Inserts a simple placeholder like [...] or [•]. The inner character gets the
 * yellow highlight.
 *
 * With text selected the placeholder REPLACES it — marking a passage as "to be
 * filled in" is what the button is for. (An earlier version always inserted at
 * a collapsed cursor so a selection could never be lost; replacing is the
 * explicitly requested behaviour. Undo restores the text.) Spacing at the edges
 * of the selection survives, so " foo " becomes " […] " and not "[…]".
 * With nothing selected, the placeholder goes in at the cursor as before.
 */
export async function insertSimplePlaceholder(char: string): Promise<void> {
  await runBatch(async (context) => {
    const selection = context.document.getSelection();
    selection.load('text');
    await context.sync();

    const { lead, core, trail } = splitEdgeSpacing(selection.text ?? '');
    // A selection of nothing but spacing counts as no selection.
    const replacing = core.length > 0;

    const openBracket = replacing
      ? selection.insertText(`${lead}[`, Word.InsertLocation.replace)
      : selection.getRange(Word.RangeLocation.end).insertText('[', Word.InsertLocation.after);
    openBracket.font.highlightColor = '';

    const inner = openBracket.insertText(char, Word.InsertLocation.after);
    inner.font.highlightColor = PLACEHOLDER_HIGHLIGHT;

    const closeBracket = inner.insertText(']', Word.InsertLocation.after);
    closeBracket.font.highlightColor = '';

    // Trailing spacing is its OWN range so the caret can land between `]` and
    // the restored space (see below), not after it.
    if (replacing && trail) {
      const trailRange = closeBracket.insertText(trail, Word.InsertLocation.after);
      trailRange.font.highlightColor = '';
    }

    // Caret after the placeholder, so typing continues where the user is looking
    // (issue #20). Word only moves the CARET here — keyboard focus is handed to
    // the document separately, see the note in utils/office/focus.ts.
    closeBracket.select(Word.SelectionMode.end);

    await context.sync();
  });
}

/**
 * Wraps text in brackets with italic + yellow highlight.
 * Uses selection if available, otherwise uses textOverride.
 */
export async function markTextAsPlaceholder(textOverride?: string): Promise<void> {
  await runBatch(async (context) => {
    const selection = context.document.getSelection();
    selection.load('text');
    await context.sync();

    // Spacing selected along with the text stays outside the brackets — it is
    // written back around them instead of being replaced away.
    const { lead, core, trail } = splitEdgeSpacing(selection.text ?? '');
    const selectedText = (textOverride ?? core)?.trim();
    if (!selectedText) {
      throw new Error('Kein Text vorhanden.');
    }

    // Lead/trail ride along with the brackets: same formatting (plain, not
    // highlighted), so no extra ranges are needed.
    const openBracket = selection.insertText(`${lead}[`, Word.InsertLocation.replace);
    openBracket.font.italic = false;
    openBracket.font.highlightColor = '';

    const inner = openBracket.insertText(selectedText, Word.InsertLocation.after);
    inner.font.italic = true;
    inner.font.highlightColor = PLACEHOLDER_HIGHLIGHT;

    const closeBracket = inner.insertText(']', Word.InsertLocation.after);
    closeBracket.font.italic = false;
    closeBracket.font.highlightColor = '';

    // Trailing spacing gets its own range so the caret can sit right after `]`.
    if (trail) {
      const trailRange = closeBracket.insertText(trail, Word.InsertLocation.after);
      trailRange.font.italic = false;
      trailRange.font.highlightColor = '';
    }

    // Caret after the markup (issue #21).
    closeBracket.select(Word.SelectionMode.end);

    await context.sync();
  });
}

/**
 * Wraps text in yellow-highlighted brackets (only brackets are yellow).
 * Uses selection if available, otherwise uses textOverride.
 */
export async function insertYellowBrackets(textOverride?: string): Promise<void> {
  await runBatch(async (context) => {
    const selection = context.document.getSelection();
    selection.load('text');
    await context.sync();

    const { lead, core, trail } = splitEdgeSpacing(selection.text ?? '');
    const selectedText = (textOverride ?? core)?.trim();
    if (!selectedText) {
      throw new Error('Kein Text vorhanden.');
    }

    // Here the BRACKETS carry the highlight, so selected spacing cannot ride
    // along with them — it gets its own unhighlighted range on each side.
    let openBracket: Word.Range;
    if (lead) {
      const leadRange = selection.insertText(lead, Word.InsertLocation.replace);
      leadRange.font.highlightColor = '';
      openBracket = leadRange.insertText('[', Word.InsertLocation.after);
    } else {
      openBracket = selection.insertText('[', Word.InsertLocation.replace);
    }
    openBracket.font.highlightColor = PLACEHOLDER_HIGHLIGHT;

    const inner = openBracket.insertText(selectedText, Word.InsertLocation.after);
    inner.font.highlightColor = '';

    const closeBracket = inner.insertText(']', Word.InsertLocation.after);
    closeBracket.font.highlightColor = PLACEHOLDER_HIGHLIGHT;

    if (trail) {
      const trailRange = closeBracket.insertText(trail, Word.InsertLocation.after);
      trailRange.font.highlightColor = '';
    }

    // Caret after the closing bracket (issue #21). Deliberately the bracket and
    // not the trailing-spacing range: the caret belongs against the markup.
    closeBracket.select(Word.SelectionMode.end);

    await context.sync();
  });
}
